import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner, Alert, Form } from "react-bootstrap";
import { FaSyncAlt, FaFileExcel } from "react-icons/fa";
import { apiFetch, getToken } from "./utils/auth";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleString(); };

// =============================================================================
// AssignShipments — admin side of the dispatcher work queue (SQL/048, 00081).
// Upload the daily Excel (OrderNum | SalesRepCode): the whole queue is
// REPLACED (previous generation archived), and any bad row rejects the whole
// file. Below the upload: the live queue with dispatcher/status filters and
// per-row reassignment to cover absences.
// =============================================================================
export default function AssignShipments() {
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [repFilter, setRepFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { ok, text, errors }
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (repFilter) params.set("salesRepCode", repFilter);
      if (statusFilter) params.set("status", statusFilter);
      const r = await apiFetch(`${API_URL}/api/dispatch?${params.toString()}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.error || `Couldn't load assignments (HTTP ${r.status}).`);
      setRows(d.rows || []);
    } catch (e) { setError(e.message); setRows([]); }
  }, [repFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/dispatch/dispatchers`);
        const d = await r.json().catch(() => ({}));
        if (r.ok && d.success !== false) setUsers(d.users || []);
      } catch { /* the pickers degrade to raw code entry via filters */ }
    })();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // multipart — bypass the JSON helper (it forces Content-Type), keep the
      // bearer token. (The installment-PO upload does this too, but reads the
      // wrong sessionStorage key — getToken() is the real one.)
      const token = getToken();
      const r = await fetch(`${API_URL}/api/dispatch/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) {
        setUploadResult({ ok: false, errors: d.errors || [d.error || `Upload failed (HTTP ${r.status}).`] });
        return;
      }
      setUploadResult({
        ok: true,
        text: `Loaded ${d.inserted} assignment${d.inserted === 1 ? "" : "s"}`
          + (d.archived ? ` — the previous ${d.archived} row${d.archived === 1 ? "" : "s"} were archived.` : "."),
      });
      await load();
    } catch (e) {
      setUploadResult({ ok: false, errors: [e.message] });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reassign = async (row, toRepCode) => {
    if (!toRepCode || toRepCode === row.salesRepCode) return;
    const target = users.find((u) => u.salesRepCode === toRepCode);
    if (!window.confirm(`Reassign order #${row.orderNum} from ${row.dispatcherName || row.salesRepCode} to ${target?.name || toRepCode}?`)) return;
    setBusyId(row.id);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dispatch/${row.id}/reassign?toRepCode=${encodeURIComponent(toRepCode)}`,
        { method: "PATCH" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.error || `Reassign failed (HTTP ${r.status}).`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div className="qw-root content-halo" style={{ maxWidth: 1100, margin: "24px auto", padding: "28px 24px 36px", borderRadius: 16 }}>
      <div className="qw-eyebrow">Dispatch queue</div>
      <h2 className="qw-h2" style={{ margin: 0 }}>Assign Shipments</h2>

      {/* ── Daily Excel upload ─────────────────────────────────────────── */}
      <div className="qw-card-flat qw-pad" style={{ marginTop: 14, marginBottom: 18 }}>
        <div className="qw-eyebrow" style={{ marginBottom: 6 }}>Daily upload</div>
        <p style={{ fontSize: 13, color: "#64748B", marginBottom: 10 }}>
          An .xlsx with two columns — <strong>OrderNum</strong> and <strong>SalesRepCode</strong> (header row required).
          Uploading <strong>replaces the whole queue</strong>; the previous rows are archived. If any row is bad
          (unknown rep, unknown or closed order, duplicate), the <strong>whole file is rejected</strong> and nothing changes.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input ref={fileRef} type="file" accept=".xlsx" disabled={uploading}
            onChange={(e) => upload(e.target.files?.[0])} style={{ fontSize: 13 }} />
          {uploading && <span style={{ fontSize: 13, color: "#64748B" }}><Spinner animation="border" size="sm" /> Validating &amp; loading…</span>}
          {!uploading && <FaFileExcel style={{ color: "#16A34A" }} />}
        </div>
        {uploadResult && (
          <Alert variant={uploadResult.ok ? "success" : "danger"} style={{ fontSize: 13, marginTop: 12, marginBottom: 0 }}>
            {uploadResult.ok ? uploadResult.text : (
              <>
                <div style={{ fontWeight: 700 }}>File rejected — fix these rows and upload again (nothing was changed):</div>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {uploadResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </>
            )}
          </Alert>
        )}
      </div>

      {/* ── Queue + filters ────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <label className="qw-label">Dispatcher</label>
          <Form.Select size="sm" className="qw-input" value={repFilter} onChange={(e) => setRepFilter(e.target.value)} style={{ minWidth: 220 }}>
            <option value="">All dispatchers</option>
            {users.map((u) => <option key={u.salesRepCode} value={u.salesRepCode}>{u.name} · {u.salesRepCode}</option>)}
          </Form.Select>
        </div>
        <div>
          <label className="qw-label">Status</label>
          <Form.Select size="sm" className="qw-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">Any</option>
            <option value="Assigned">Assigned</option>
            <option value="Completed">Completed</option>
          </Form.Select>
        </div>
        <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={() => { setRows(null); load(); }}>
          <FaSyncAlt style={{ marginRight: 6 }} /> Refresh
        </button>
      </div>

      {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}

      {rows == null ? (
        <div style={{ textAlign: "center", padding: 30, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="qw-card-flat qw-pad" style={{ textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
          No assignments{repFilter || statusFilter ? " match these filters" : " loaded yet — upload today's Excel above"}.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#64748B", fontSize: 12 }}>
                <th style={{ padding: "6px 8px" }}>Order</th>
                <th style={{ padding: "6px 8px" }}>Customer</th>
                <th style={{ padding: "6px 8px" }}>PO</th>
                <th style={{ padding: "6px 8px" }}>Dispatcher</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>Completed</th>
                <th style={{ padding: "6px 8px" }}>Reassign to</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const completed = row.status === "Completed";
                return (
                  <tr key={row.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 600 }} className="qw-mono">#{row.orderNum}</td>
                    <td style={{ padding: "6px 8px" }}>{row.customerName || "—"}{row.customerCustID ? ` · ${row.customerCustID}` : ""}</td>
                    <td style={{ padding: "6px 8px" }} className="qw-mono">{row.poNum || "—"}</td>
                    <td style={{ padding: "6px 8px" }}>
                      {row.dispatcherName || row.salesRepCode}
                      <span style={{ color: "#94A3B8" }}> · {row.salesRepCode}</span>
                      {row.reassignedFrom && <div style={{ fontSize: 11, color: "#94A3B8" }}>was {row.reassignedFrom}</div>}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      {completed
                        ? <span className="qw-pill qw-pill-emerald">COMPLETED</span>
                        : <span className="qw-pill qw-pill-indigo">ASSIGNED</span>}
                      {completed && row.orderOpen === true && (
                        <div style={{ fontSize: 11, color: "#B45309", marginTop: 2 }}>order still open (short-shipped)</div>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px", fontSize: 12, color: "#64748B" }}>
                      {completed
                        ? <>{fmtDate(row.completedAt)}{row.packNum ? <div>Pack #{row.packNum}</div> : null}</>
                        : "—"}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      {completed ? <span style={{ color: "#94A3B8" }}>—</span> : (
                        <Form.Select size="sm" className="qw-input" disabled={busyId === row.id}
                          value={row.salesRepCode} onChange={(e) => reassign(row, e.target.value)} style={{ minWidth: 180 }}>
                          {/* Current holder first, then everyone else */}
                          {!users.some((u) => u.salesRepCode === row.salesRepCode) && (
                            <option value={row.salesRepCode}>{row.salesRepCode}</option>
                          )}
                          {users.map((u) => (
                            <option key={u.salesRepCode} value={u.salesRepCode}>{u.name} · {u.salesRepCode}</option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
